import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './DayDetailsDrawer.css';
import LoadingSpinner from '../LoadingSpinner';
import { fetchDayDevices } from '../../services/shipmentsApi';
import { getTimeZone } from './timezone';
import { formatDDMMYYYY } from '../../utils/dates';

const MAX_LIMIT = 2000;
const DEFAULT_LIMIT = 500;

const dateFormatter = new Intl.DateTimeFormat('en-GB', {
  dateStyle: 'full',
  timeZone: getTimeZone()
});

function useKeyboardClose(enabled, onClose) {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [enabled, onClose]);
}

function resolveFriendlyDate(isoDate) {
  if (!isoDate) return '';
  try {
    return dateFormatter.format(new Date(`${isoDate}T00:00:00`));
  } catch (err) {
    return formatDDMMYYYY(isoDate);
  }
}

export default function DayDetailsDrawer({ isoDate, displayDate, onClose, isServiceUser }) {
  const isOpen = Boolean(isoDate);
  const navigate = useNavigate();
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [details, setDetails] = useState(null);

  useKeyboardClose(isOpen, onClose);

  useEffect(() => {
    if (!isOpen) {
      setDetails(null);
      setError(null);
      setStatus(null);
      setLimit(DEFAULT_LIMIT);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    (async () => {
      setLoading(true);
      setStatus('Loading devices…');
      setError(null);
      try {
        const response = await fetchDayDevices({
          date: isoDate,
          limit,
          signal: controller.signal,
          onRetry: ({ attempt, total }) => {
            if (cancelled) return;
            setStatus(`Retrying… (${attempt}/${total})`);
          }
        });
        if (cancelled) return;
        if (response?.ok === false) {
          if (response?.status === 403 || response?.error === 'forbidden') {
            if (!isServiceUser) {
              setError("You don't have access to this resource.");
            } else {
              setError('Failed to load devices for this day (auth). Please refresh or contact support.');
            }
            setDetails(null);
            setStatus(null);
            return;
          }
          throw new Error(response?.error || 'Failed to load devices');
        }
        setDetails(response);
        setStatus(null);
      } catch (err) {
        if (!cancelled) {
          if (err?.name === 'AbortError') return;
          const friendly = err instanceof TypeError
            ? "Couldn't reach server. Please try again."
            : err?.message || 'Failed to load devices';
          setError(friendly);
          setStatus(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isoDate, isOpen, limit, isServiceUser]);

  const deviceRows = useMemo(() => Array.isArray(details?.devices) ? details.devices : [], [details]);
  const hasShipments = (details?.count || 0) > 0;
  const hasDeviceRows = deviceRows.length > 0;
  const friendlyDate = resolveFriendlyDate(isoDate);
  const baseDisplayDate = displayDate || formatDDMMYYYY(isoDate);
  const hasMore = details?.count > deviceRows.length;

  const handleDeviceClick = useCallback(device => {
    if (device?._id) {
      navigate(`/device/${device._id}`);
      onClose?.();
    }
  }, [navigate, onClose]);

  const handleLoadMore = useCallback(() => {
    setLimit(prev => Math.min(prev + DEFAULT_LIMIT, MAX_LIMIT));
  }, []);

  if (!isOpen) return null;

  const content = (
    <div className="shipments-drawer-overlay" onClick={onClose}>
      <div
        className="shipments-drawer"
        onClick={event => event.stopPropagation()}
      >
        <header>
          <div>
            <h2>{baseDisplayDate || friendlyDate}</h2>
            {details?.count != null && (
              <span className="badge">Devices shipped: {details.count}</span>
            )}
          </div>
          <button type="button" className="close-button" onClick={onClose}>
            Close
          </button>
        </header>
        {friendlyDate && (
          <div style={{ marginTop: '0.25rem', color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
            {friendlyDate}
          </div>
        )}
        {status && !error && <div className="drawer-status">{status}</div>}
        {error && <div className="drawer-error">{error}</div>}
        {loading && !details && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem 0' }}>
            <LoadingSpinner size={48} />
          </div>
        )}
        {details && (
          <div className="drawer-content">
            <section>
              <h3 style={{ margin: '0 0 0.75rem 0' }}>Devices</h3>
              {hasShipments ? (
                hasDeviceRows ? (
                  <div className="devices-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product Number</th>
                          <th>Service Tag</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deviceRows.map(device => {
                          const key = device?._id || `${device.serviceTag || ''}-${device.productNumber || ''}`;
                          const isClickable = Boolean(device?._id);
                          return (
                            <tr
                              key={key}
                              onClick={isClickable ? () => handleDeviceClick(device) : undefined}
                              className={isClickable ? 'is-clickable' : ''}
                            >
                              <td>{device?.productNumber || '—'}</td>
                              <td>{device?.serviceTag || '—'}</td>
                              <td>{device?.scandate_str || baseDisplayDate}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="drawer-status" style={{ textAlign: 'center' }}>
                    Devices exist for this day, but no rows are available.
                  </div>
                )
              ) : (
                <div className="drawer-status" style={{ textAlign: 'center' }}>
                  No devices shipped on this day.
                </div>
              )}
            </section>
          </div>
        )}
        <div className="drawer-actions">
          <button type="button" className="close-button" onClick={onClose}>
            Close
          </button>
          {hasMore && limit < MAX_LIMIT && (
            <button
              type="button"
              className="load-more"
              onClick={handleLoadMore}
              disabled={loading}
            >
              Load more
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const target = document.getElementById('modal-root');
  if (target) {
    return createPortal(content, target);
  }
  return content;
}
